import type { PortalHome, PortalNotice } from '@central/api';
import {
  Card,
  KpiTile,
  PageHeader,
  color,
  daysUntil,
  formatDate,
  status,
  formatPercent,
} from '@central/ui';
import { Button } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouteContext } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { homeQuery } from '../api';
import { ComposeButton } from '../components/ComposeButton';
import { DeploymentGrid } from '../components/DeploymentCardView';
import { QueryState } from '../components/QueryState';
import { RequestRenewal } from '../components/RequestRenewal';
import { StripLegend } from '../components/StripLegend';

function greeting(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** "2 of 3 deployments healthy · one needs a look". */
function healthLine({ healthy, deployments }: PortalHome['kpis']): string {
  const line = `${healthy} of ${deployments} deployment${deployments === 1 ? '' : 's'} healthy`;
  const unwell = deployments - healthy;
  if (unwell <= 0) return line;
  return `${line} · ${unwell === 1 ? 'one needs' : `${unwell} need`} a look`;
}

export function Home() {
  const { me } = useRouteContext({ from: '/app' });
  const { data, error } = useQuery(homeQuery);
  return (
    <>
      <PageHeader
        title={`${greeting(new Date().getHours())}, ${me.user.name}`}
        subtitle={data && healthLine(data.kpis)}
      />
      {data ? <HomeBody home={data} /> : <QueryState error={error} />}
    </>
  );
}

function HomeBody({ home }: { home: PortalHome }) {
  const { kpis, customer } = home;
  const m = customer.maintenance;
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
          gap: 12,
        }}
      >
        <KpiTile label="Deployments" value={kpis.deployments} />
        <KpiTile label="Uptime · 30 d" value={formatPercent(kpis.uptime30d)} />
        <LicenseExpiryTile at={kpis.nextLicenseExpiresAt} />
        {m.coversThrough && (
          <KpiTile
            label="Maintenance covers releases through"
            value={
              <>
                <span style={{ fontSize: 22 }}>{m.coversThrough}</span>
                {m.endsAt && <TileNote>plan ends {formatDate(m.endsAt)}</TileNote>}
              </>
            }
          />
        )}
      </div>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>Deployments</h2>
        {home.deployments.length > 0 ? (
          <>
            <DeploymentGrid items={home.deployments} />
            <StripLegend />
          </>
        ) : (
          <Card>No deployments are reporting to Central yet.</Card>
        )}
      </section>
      {home.notices.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {home.notices.map((n, i) => (
            <Notice key={i} n={n} />
          ))}
        </div>
      )}
    </>
  );
}

function TileNote({ children, tone }: { children: ReactNode; tone?: string }) {
  return (
    <span
      style={{ display: 'block', fontSize: 13, fontWeight: 400, color: tone ?? color.textMuted }}
    >
      {children}
    </span>
  );
}

/** The soonest expiry of a license in use; amber within the 60-day warning window. */
function LicenseExpiryTile({ at }: { at?: string }) {
  if (!at) return <KpiTile label="License expires" value="–" />;
  const days = daysUntil(at);
  return (
    <KpiTile
      label="License expires"
      value={
        <>
          <span style={{ fontSize: 22 }}>{formatDate(at)}</span>
          <TileNote tone={days <= 60 ? status.warn.fg : undefined}>
            in {days} day{days === 1 ? '' : 's'}
          </TileNote>
        </>
      }
    />
  );
}

/** What each kind of notice lets the customer do next. */
function NoticeActions({ n }: { n: PortalNotice }) {
  switch (n.kind) {
    case 'release': {
      const version = n.releaseVersion ?? '';
      return (
        <>
          <Button component={Link} to="/releases" h={40}>
            Release notes
          </Button>
          <ComposeButton kind="question" subject={`Schedule an upgrade to ${version}`.trim()}>
            Schedule upgrade
          </ComposeButton>
        </>
      );
    }
    case 'seats':
      return (
        <ComposeButton kind="license" subject={`More seats: ${n.title}`}>
          Request more seats
        </ComposeButton>
      );
    case 'incident':
      return n.deploymentId ? (
        <Button
          renderRoot={(props) => (
            <Link
              {...props}
              to="/deployments/$deploymentId"
              params={{ deploymentId: n.deploymentId! }}
            />
          )}
          variant="default"
          h={40}
        >
          View deployment
        </Button>
      ) : null;
    case 'license_expiry':
      return n.licenseId ? <RequestRenewal licenseId={n.licenseId} /> : null;
    case 'maintenance':
      return (
        <ComposeButton kind="renewal" subject={`Renewal: ${n.title}`}>
          Ask about renewal
        </ComposeButton>
      );
  }
}

function Notice({ n }: { n: PortalNotice }) {
  return (
    <Card title={n.title} style={{ flex: '1 1 320px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p
          data-tone={n.tone}
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: '21px',
            color: n.tone === 'warn' ? status.warn.fg : color.textBody,
          }}
        >
          {n.body}
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <NoticeActions n={n} />
        </div>
      </div>
    </Card>
  );
}
